import type { ServiceDescription } from '../../Descriptors/ServiceDescription';
import type { ClientConfigParams } from '../Interfaces/ConfigParams';
import type { HttpVerb } from '../Interfaces/Http';
import { assertValidAasResponse, headersLength } from '../../Utils/Restutils';

export abstract class ClientBase {
  public readonly baseUrl: URL;
  protected headers: Headers = new Headers();

  constructor(
    protected readonly configParams: ClientConfigParams,
  ) {
    const protocol = configParams.protocol ?? 'https';
    const port = configParams.port ? `:${configParams.port}` : '';
    const basePath = configParams.basePath
      ? `${configParams.basePath.replace(/\/+$/, '')}/`
      : '';
    const url = `${protocol}://${configParams.host}${port}`;
    this.baseUrl = new URL(basePath, url);
  }

  public async request(
    method: HttpVerb,
    path: string,
    params?: URLSearchParams,
    body?: BodyInit,
    signal?: AbortSignal,
  ): Promise<[Request, Response]> {
    const url = new URL(path.replace(/^\/+/, ''), this.baseUrl);
    url.search = params?.toString() ?? '';
    const headers = this.getHeaders();
    if (headersLength(this.headers) === 0) {
      // if no headers were set, then edit the default headers
      if (['get', 'delete'].includes(method)) {
        // get and delete requests should not have a body
        headers.delete('Content-Type');
      }
      if (['put', 'patch', 'delete'].includes(method)) {
        // put, patch and delete usually don't get a response body
        headers.delete('Accept');
      }
      // post by default usually has a body and gets a response body
    }
    const request = new Request(url, {
      headers,
      method,
      body,
      signal,
    });
    const response = await fetch(request);
    // reset the headers
    this.headers = new Headers();
    return [request, response];
  }

  protected getHeaders(): Headers {
    let headers: Headers;
    if (headersLength(this.headers) > 0) {
      // use the supplied headers
      headers = new Headers(this.headers);
    }
    else {
      // use default headers
      headers = new Headers({
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      });
    }
    const auth = this.configParams.auth;
    if (auth) {
      switch (auth.type) {
        case 'basic':
        {
          const credentials = btoa(`${auth.username}:${auth.password}`);
          headers.set('Authorization', `Basic ${credentials}`);
          break;
        }
        case 'token':
          headers.set('Authorization', `Bearer ${auth.token}`);
          break;
        case 'cert': // TODO: is this common
          headers.set('X-Client-Cert', auth.cert);
          break;
        default: // TODO: specific error
          throw new Error('Unsupported auth type');
      }
    }
    return headers;
  };

  public async getServiceDescription(
    signal?: AbortSignal,
  ): Promise<ServiceDescription> {
    const [request, response] = await this.request(
      'get',
      '/description',
      undefined,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response);
  }
};
